import { useEffect, useRef } from 'react';
import { AppEventsCapability } from '@doxyme/extensions-bridge';
import { useAddCapability } from '@doxyme/extensions-bridge-react';
import { bridge } from '../../../features/extensions/instances';
import { useExtensionsAppEvents } from './useExtensionsAppEvents';
import { callActor } from '../../../features/call';
import { CallState } from '../../../features/call/call.types';
import { session } from '../../../webrtc/session';
import { subscribeToLeave } from '@doxyme/frontend/features/realtime/realtime.subscribe';
import { RealtimeState } from '@doxyme/realtime';
import { RealtimeMemberStatus } from '@doxyme/frontend/features/realtime/realtime.types';
import { getLogger } from '../../../libs/logger';

const logger = getLogger('useExtensionsAddAppEvents', {
  component: 'extensions',
  lib: 'frontend',
});

export function useExtensionsAddAppEvents(): void {
  useAddCapability(bridge, () => new AppEventsCapability());

  const { sendSessionEnded, sendProviderJoined, sendProviderLeft } =
    useExtensionsAppEvents();

  /**
   * We store the event sender functions in a ref to avoid re-subscribing to
   * callActor.subscribe whenever these callbacks change. The subscribe
   * handler is registered once on mount with an empty dependency array, but we
   * still need access to the latest sender functions. Using a ref allows us to
   * always call the current version of these functions without causing the
   * effect to re-run.
   */
  const sendersRef = useRef({
    sendSessionEnded,
    sendProviderJoined,
    sendProviderLeft,
  });

  useEffect(() => {
    sendersRef.current = {
      sendSessionEnded,
      sendProviderJoined,
      sendProviderLeft,
    };
  }, [sendSessionEnded, sendProviderJoined, sendProviderLeft]);

  const prevStateRef = useRef<CallState | null>(null);

  useEffect(() => {
    /**
     * We use subscribe instead of a React effect with state dependency because:
     * 1. It guarantees all state transitions are captured - React effects may miss
     *    intermediate states if multiple transitions happen in rapid succession
     * 2. It runs synchronously with the state machine, independent of React's render cycle
     * 3. This ensures extension events accurately reflect the actual call lifecycle
     */
    const subscription = callActor.subscribe((state) => {
      const currentState = parseCallState(state.value);
      const previousState = prevStateRef.current;
      const { sessionId } = session;

      const senders = sendersRef.current;

      if (sessionId && currentState !== previousState) {
        if (currentState === CallState.RedirectingToDashboard) {
          senders.sendProviderLeft({ sessionId });
        }

        if (currentState === CallState.InProgress) {
          senders.sendProviderJoined({ sessionId });
        }

        if (
          currentState === CallState.Leaving ||
          currentState === CallState.LeavingAfterError
        ) {
          senders.sendSessionEnded({ sessionId });
        }
      }

      prevStateRef.current = currentState;
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Handles session endings not captured by the callActor state machine.
   * These occur when the provider ends a call without being actively in it:
   * 1. Provider ends a group call from the dashboard/waiting room
   * 2. Provider ends a group call while inside a different call
   */
  useEffect(() => {
    return subscribeToLeave((event) => {
      const state = event.data?.state as RealtimeState | undefined;
      if (state?.['status'] === RealtimeMemberStatus.OnHold) {
        const sessionId = state?.['sessionId'] as string | undefined;
        if (!sessionId) {
          logger.error(
            'SessionId is not provided for sendSessionEnded, session was on hold',
          );
          return;
        }
        sendSessionEnded({ sessionId, sessionWasOnHold: true });
      }
    });
  }, [sendSessionEnded]);
}

function parseCallState(
  stateValue: string | Record<string, unknown>,
): CallState {
  return (
    typeof stateValue === 'string' ? stateValue : Object.keys(stateValue)[0]
  ) as CallState;
}
